import { Controller } from "@hotwired/stimulus"

// FD-695: the work item title was a single-line text_field, so a title wider than
// its pane clipped with no ellipsis — worst in the narrow intake triage pane, where
// a 45-character title ran off the edge. It's a textarea now; this grows it to fit
// instead of scrolling, and keeps a title single-line by swallowing Enter.
export default class extends Controller {
  connect() {
    // FD-1296: inside <cs-page> the field isn't laid out until the page's first render has
    // placed its slot, and a height measured before then was 0px. The title collapsed for a
    // frame on every item page, and on Back its 22px reappearing above the restored offset
    // made scroll anchoring move the pane (MainPaneScrollTest). Size it once the page has
    // rendered; with no <cs-page> that is a microtask away.
    const page = this.element.closest("cs-page")
    Promise.resolve(page?.updateComplete).then(() => { if (this.element.isConnected) this.resize() })

    // A turbo morph can hand the element back without firing connect() again, which
    // would leave the height we measured for the *previous* value in place.
    this.onMorph = () => this.resize()
    document.addEventListener("turbo:morph", this.onMorph)

    // Width drives how many lines the title wraps to, and it changes without any
    // input event: window resize, sidebar collapse, the pane itself resizing. Watch
    // width only — reacting to the height we write here would loop.
    this.lastWidth = this.element.clientWidth
    this.observer = new ResizeObserver(() => {
      if (this.element.clientWidth === this.lastWidth) return
      this.lastWidth = this.element.clientWidth
      this.resize()
    })
    this.observer.observe(this.element)
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.onMorph)
    this.observer?.disconnect()
  }

  resize() {
    const el = this.element
    // Not laid out (<cs-page> hasn't placed it yet, or a hidden pane): its scrollHeight is
    // 0, and writing that would collapse the field until something resized it again.
    if (el.getClientRects().length === 0) return
    el.style.height = "auto"
    el.style.height = `${el.scrollHeight}px`
  }

  // Titles are single-line, so Enter must not insert a newline. Suppress it and blur,
  // which fires `change` and saves through auto-submit — the same path clicking away
  // takes (FD-603). As a text_field this came free from the browser's implicit form
  // submission; a textarea has to ask. Without the blur, Enter silently does nothing
  // and the edit sits unsaved, which reads as data loss.
  //
  // Blur here rather than calling requestSubmit(): blur fires change exactly once, so
  // one PATCH. requestSubmit() would send one now and a second on the later blur.
  commit(event) {
    if (event.key !== "Enter") return
    event.preventDefault()
    this.element.blur()
  }
}
